import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import {
  ArrowLeft,
  Bell,
  ChevronRight,
  ExternalLink,
  Loader2,
  MapPin,
  Phone,
  TriangleAlert,
} from 'lucide-react';
import { MainLayout } from '../layouts/MainLayout';
import { Meter } from '../components/Meter';
import { RequestForm } from '../components/RequestForm';
import { SearchContextBar } from '../components/SearchContextBar';
import { AvailabilitySnapshot } from '../components/AvailabilitySnapshot';
import { FirstComeFirstServedCard } from '../components/FirstComeFirstServedCard';
import { WeatherCard } from '../components/WeatherCard';
import { LocationMap } from '../components/LocationMap';
import { usePlaceDetails, useAvailableFacilitiesForPlace } from '../hooks/useApi';
import { useAuth } from '../contexts/AuthContext';
import { usePageMeta } from '../hooks/usePageMeta';
import { JsonLd } from '../components/JsonLd';
import { ExpandableText } from '../components/ExpandableText';
import { trackViewPark } from '../lib/posthog';
import { computeEndDate, formatIsoDateShort } from '../utils/dates';

function stripHtml(text: string): string {
  return text.replace(/<[^>]+>/g, '').trim();
}

function bookedDotClass(pct: number): string {
  if (pct >= 0.97) return 'bg-red-500';
  if (pct >= 0.8) return 'bg-amber-500';
  return 'bg-emerald-500';
}

function shortParkName(name: string): string {
  const trimmed = name
    .replace(/\s+(National|State)\s+(Park|Forest|Monument|Preserve|Seashore|Lakeshore|Recreation Area|Historical? Park)$/i, '')
    .trim();
  return trimmed || name;
}

function hasRenderableCoordinates(
  latitude: number | null | undefined,
  longitude: number | null | undefined,
): boolean {
  if (typeof latitude !== 'number' || typeof longitude !== 'number') return false;
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return false;
  if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) return false;
  return !(latitude === 0 && longitude === 0);
}

export function ParkPage() {
  const { id } = useParams({ from: '/park/$id' });
  const searchParams = useSearch({ from: '/park/$id' });
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { data: place, isLoading, error } = usePlaceDetails(id);

  usePageMeta({
    title: place ? `${place.placeName} - Campgrounds & Availability | HellaCamping` : undefined,
    description: place?.description?.trim() || (place ? `Explore campgrounds and availability at ${place.placeName} on HellaCamping.` : undefined),
    canonicalPath: `/park/${id}`,
    ogImage: place?.heroImageUrl ?? undefined,
  });

  useEffect(() => {
    if (place) {
      trackViewPark(id, place.placeName);
    }
  }, [id, place]);

  const [requestFacilityId, setRequestFacilityId] = useState<string | null>(null);
  const [heroIndex, setHeroIndex] = useState(0);
  const [showAllAlerts, setShowAllAlerts] = useState(false);

  const endDate = useMemo(() => {
    if (!searchParams.startDate || !searchParams.nights) return undefined;
    return computeEndDate(searchParams.startDate, searchParams.nights);
  }, [searchParams.startDate, searchParams.nights]);

  const hasSearchContext = !!(searchParams.startDate && endDate);

  const { data: availableFacilityIds, isLoading: isLoadingAvailability } = useAvailableFacilitiesForPlace(
    id,
    {
      startDate: searchParams.startDate ?? '',
      endDate: endDate ?? '',
      minNights: searchParams.nights,
    },
    hasSearchContext,
  );

  const availableFacilityIdSet = useMemo(
    () => new Set<string>(availableFacilityIds ?? []),
    [availableFacilityIds],
  );

  const parkLd = useMemo(() => {
    if (!place) return null;
    return {
      '@context': 'https://schema.org',
      '@type': 'Park',
      name: place.placeName,
      description: place.description || undefined,
      geo: { '@type': 'GeoCoordinates', latitude: place.latitude, longitude: place.longitude },
      ...(place.heroImageUrl ? { image: place.heroImageUrl } : {}),
      url: `https://hellacamping.com/park/${id}`,
    };
  }, [place, id]);

  const parkBreadcrumbLd = useMemo(() => {
    if (!place) return null;
    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://hellacamping.com/' },
        { '@type': 'ListItem', position: 2, name: place.placeName },
      ],
    };
  }, [place]);

  if (isLoading) {
    return (
      <MainLayout>
        <div className="flex flex-col items-center justify-center py-16">
          <Loader2 className="w-10 h-10 animate-spin text-emerald-600 mb-4" />
          <p className="text-gray-600">Loading park…</p>
        </div>
      </MainLayout>
    );
  }

  if (error || !place) {
    return (
      <MainLayout>
        <div className="max-w-4xl mx-auto py-16">
          <p className="text-gray-700">Park not found.</p>
          <button
            onClick={() => navigate({
              to: '/search',
              search: {
                startDate: searchParams.startDate,
                nights: searchParams.nights,
                guests: searchParams.guests,
              },
            })}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Search
          </button>
        </div>
      </MainLayout>
    );
  }

  // Defensive against deploy skew: a cached frontend can briefly see an older
  // API payload without the newer array fields — never crash, just degrade.
  const galleryImages = (place.images ?? []).length > 0
    ? place.images!
    : place.heroImageUrl
      ? [place.heroImageUrl]
      : [];
  const heroSrc = galleryImages[heroIndex] ?? galleryImages[0] ?? '/placeholder-campground.svg';
  const stats = place.stats ?? null;
  // Facility notices are overwhelmingly per-campground boilerplate (rules,
  // firewood, bear lockers) repeated across every campground — they live on
  // each facility page and as per-row badges below. At park level we surface
  // only CLOSURES, deduped by text.
  const alerts = place.alerts ?? [];
  const closureAlerts = alerts.filter((a) =>
    /closed for the .* season|closed until|currently closed|closure/i.test(a.text)
  );
  const dedupedClosures = [...new Map(closureAlerts.map((a) => [stripHtml(a.text), a])).values()];
  const visibleClosures = showAllAlerts ? dedupedClosures : dedupedClosures.slice(0, 3);
  const shortName = shortParkName(place.placeName);
  const statsPct = stats ? Math.round(stats.bookedPct * 100) : null;
  const hasParkMap = hasRenderableCoordinates(place.latitude, place.longitude);

  return (
    <MainLayout>
      {parkLd && <JsonLd data={parkLd} />}
      {parkBreadcrumbLd && <JsonLd data={parkBreadcrumbLd} />}
      <div className="max-w-6xl mx-auto space-y-6">
        <SearchContextBar
          startDate={searchParams.startDate}
          nights={searchParams.nights}
          guests={searchParams.guests}
        />

        {/* Hero */}
        <div>
          <div className="h-72 sm:h-96 rounded-xl overflow-hidden relative bg-gray-100">
            <img
              src={heroSrc}
              alt={place.placeName}
              className="object-cover w-full h-full"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).src = '/placeholder-campground.svg';
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6">
              <h1 className="text-3xl sm:text-5xl font-bold text-white tracking-tight">
                {place.placeName}
              </h1>
              <div className="flex flex-wrap gap-2 mt-3">
                <span className="bg-white/90 backdrop-blur px-3 py-1 rounded-full text-xs font-medium text-gray-800">
                  {place.facilities.length.toLocaleString()} campground{place.facilities.length === 1 ? '' : 's'}
                </span>
                <span className="bg-white/90 backdrop-blur px-3 py-1 rounded-full text-xs font-medium text-gray-800">
                  {place.totalSites.toLocaleString()} sites
                </span>
                {stats && statsPct !== null && (
                  <span className="inline-flex items-center gap-1.5 bg-white/90 backdrop-blur px-3 py-1 rounded-full text-xs font-medium text-gray-800 tabular-nums">
                    <span className={`w-2 h-2 rounded-full ${bookedDotClass(stats.bookedPct)}`} />
                    {statsPct}% booked · next 30d
                  </span>
                )}
                {place.refreshing && !stats && (
                  <span className="inline-flex items-center gap-1.5 bg-white/90 backdrop-blur px-3 py-1 rounded-full text-xs font-medium text-gray-700">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Checking availability…
                  </span>
                )}
                {stats?.dataAgeHours != null && (
                  <span className="bg-white/90 backdrop-blur px-3 py-1 rounded-full text-xs font-medium text-gray-800 tabular-nums">
                    {stats.dataAgeHours === 0 ? 'just checked' : `checked ${stats.dataAgeHours}h ago`}
                  </span>
                )}
                {place.firstComeFirstServed && (
                  <span className="inline-flex items-center gap-1.5 bg-white/90 backdrop-blur px-3 py-1 rounded-full text-xs font-medium text-gray-800">
                    First-come, first-served
                  </span>
                )}
              </div>
            </div>
          </div>
          {galleryImages.length > 1 && (
            <div className="flex gap-2 mt-2">
              {galleryImages.slice(0, 4).map((img, index) => (
                <button
                  key={img}
                  type="button"
                  onClick={() => setHeroIndex(index)}
                  aria-label={`Show photo ${index + 1}`}
                  className={`h-14 w-20 rounded-md overflow-hidden border-2 transition-colors ${
                    index === heroIndex ? 'border-emerald-600' : 'border-transparent hover:border-gray-300'
                  }`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" loading="lazy" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Closures only — per-campground notices live on each facility page
            and as the per-row badges in the campground list below. */}
        {dedupedClosures.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <div className="flex items-center gap-2">
              <TriangleAlert className="w-4 h-4 text-red-700" />
              <h2 className="text-sm font-semibold text-red-900">Closures</h2>
            </div>
            <ul className="mt-2 space-y-1.5">
              {visibleClosures.map((alert, index) => (
                <li key={`${alert.facilityName}-${index}`} className="text-sm text-red-800 line-clamp-2">
                  <span className="font-medium">{alert.facilityName}:</span>{' '}
                  {stripHtml(alert.text)}
                </li>
              ))}
            </ul>
            {dedupedClosures.length > 3 && (
              <button
                type="button"
                onClick={() => setShowAllAlerts((v) => !v)}
                className="mt-2 text-sm font-medium text-red-900 hover:text-red-700"
              >
                {showAllAlerts ? 'Show fewer' : `+${dedupedClosures.length - 3} more`}
              </button>
            )}
          </div>
        )}

        {/* Body */}
        <div className="lg:grid lg:grid-cols-3 lg:gap-6 space-y-6 lg:space-y-0">
          <div className="lg:col-span-2 space-y-6">
            {/* About */}
            {place.description && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                <h2 className="text-lg font-bold text-gray-900 mb-3">About {shortName}</h2>
                <ExpandableText
                  text={place.description}
                  className="text-sm leading-relaxed text-gray-700 whitespace-pre-line"
                />
              </div>
            )}

            {/* Things to do */}
            {place.activities && place.activities.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                <h2 className="text-lg font-bold text-gray-900 mb-3">Things to do</h2>
                <div className="flex flex-wrap gap-2">
                  {place.activities.map((activity) => (
                    <span
                      key={activity}
                      className="bg-emerald-50 text-emerald-800 px-3 py-1.5 rounded-full text-sm font-medium"
                    >
                      {activity}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Getting there */}
            {place.directions && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                <h2 className="text-lg font-bold text-gray-900 mb-3">Getting there</h2>
                <ExpandableText
                  text={place.directions}
                  maxHeight={88}
                  className="text-sm leading-relaxed text-gray-700 whitespace-pre-line"
                />
                {place.phone && (
                  <p className="mt-3 inline-flex items-center gap-1.5 text-sm text-gray-600">
                    <Phone className="w-3.5 h-3.5 text-gray-400" />
                    Park info: {place.phone}
                  </p>
                )}
              </div>
            )}

            {/* Campgrounds */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
              <div className="flex items-baseline justify-between gap-3">
                <h2 className="text-lg font-bold text-gray-900">
                  Campgrounds ({place.facilities.length})
                </h2>
                {alerts.length > 0 && (
                  <span className="text-xs text-gray-400">
                    {alerts.length} notice{alerts.length === 1 ? '' : 's'} — see each campground
                  </span>
                )}
              </div>
              <div className="mt-2 divide-y divide-gray-100">
                {place.facilities.map((facility) => {
                  const isRequesting = requestFacilityId === facility.facilityId;
                  const hasAvailability = hasSearchContext && !isLoadingAvailability
                    ? availableFacilityIdSet.has(facility.facilityId)
                    : null;
                  const facilityPct = facility.bookedPct != null
                    ? Math.round(facility.bookedPct * 100)
                    : null;
                  const facilitySeasonClosed = facility.season?.status === 'closed';
                  const facilityReopens = formatIsoDateShort(facility.season?.reopensOn);

                  return (
                    <div key={facility.facilityId}>
                      <Link
                        to="/facility/$id"
                        params={{ id: String(facility.facilityId) }}
                        search={{
                          startDate: searchParams.startDate,
                          nights: searchParams.nights,
                          guests: searchParams.guests,
                          fromParkId: id,
                        }}
                        className="flex items-center gap-4 py-3 group"
                      >
                        {facility.imageUrl ? (
                          <img
                            src={facility.imageUrl}
                            alt=""
                            loading="lazy"
                            className="w-16 h-12 rounded-lg object-cover flex-shrink-0 bg-gray-100"
                            onError={(e) => {
                              (e.currentTarget as HTMLImageElement).src = '/placeholder-campground.svg';
                            }}
                          />
                        ) : (
                          <div className="w-16 h-12 rounded-lg bg-gray-100 flex-shrink-0" />
                        )}
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 line-clamp-1 group-hover:text-emerald-700">
                            {facility.facilityName}
                          </p>
                          <p className="text-xs text-gray-500">
                            {facility.siteCount.toLocaleString()} sites
                            {facility.closureCount > 0 && (
                              <span className="text-amber-600">
                                {' · '}
                                <TriangleAlert className="w-3 h-3 inline align-[-1px]" />{' '}
                                {facility.closureCount} notice{facility.closureCount === 1 ? '' : 's'}
                              </span>
                            )}
                            {hasAvailability !== null && (
                              <span
                                className={`ml-2 rounded-md px-1.5 py-0.5 ${
                                  hasAvailability
                                    ? 'bg-emerald-50 text-emerald-700'
                                    : 'bg-amber-50 text-amber-700'
                                }`}
                              >
                                {hasAvailability ? 'Open for your dates' : 'No openings for your dates'}
                              </span>
                            )}
                          </p>
                        </div>
                        <div className="ml-auto flex items-center gap-3 flex-shrink-0">
                          {facilitySeasonClosed ? (
                            <span className="text-xs font-medium text-red-700">
                              Closed for the season{facilityReopens ? ` · opens ${facilityReopens}` : ''}
                            </span>
                          ) : facilityPct !== null && facility.bookedPct !== null ? (
                            <div className="w-24 hidden sm:block">
                              <Meter pct={facility.bookedPct} size="sm" tone="light" />
                              <p className="mt-1 text-xs tabular-nums text-gray-600">
                                {facilityPct}% booked
                              </p>
                            </div>
                          ) : null}
                          <button
                            type="button"
                            aria-label="Get availability alerts"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (!isAuthenticated) {
                                navigate({ to: '/auth' });
                                return;
                              }
                              setRequestFacilityId((value) =>
                                value === facility.facilityId ? null : facility.facilityId
                              );
                            }}
                            className="p-2 rounded-lg hover:bg-emerald-50 text-emerald-600"
                          >
                            <Bell className="w-4 h-4" />
                          </button>
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        </div>
                      </Link>
                      {isRequesting && (
                        <div className="pb-3">
                          <RequestForm
                            facilityId={facility.facilityId}
                            initialStartDate={searchParams.startDate}
                            initialEndDate={endDate}
                            onSuccess={() => setRequestFacilityId(null)}
                            onCancel={() => setRequestFacilityId(null)}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Right rail */}
          <div className="lg:col-span-1 lg:sticky lg:top-6 self-start space-y-6">
            {/* Availability snapshot */}
            {stats ? (
              <AvailabilitySnapshot stats={stats} />
            ) : place.firstComeFirstServed ? (
              <FirstComeFirstServedCard />
            ) : null}

            {/* Map */}
            {hasParkMap ? (
              <LocationMap
                latitude={place.latitude}
                longitude={place.longitude}
                label={place.placeName}
              />
            ) : null}

            {/* Weather */}
            <WeatherCard lat={place.latitude} lon={place.longitude} />

            {/* Park facts */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
              <h2 className="text-sm font-bold text-gray-900 mb-3">Park facts</h2>
              <div className="space-y-2 text-sm text-gray-600">
                <p className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <span className="tabular-nums">
                    {place.latitude.toFixed(5)}, {place.longitude.toFixed(5)}
                  </span>
                </p>
                {place.phone && (
                  <p className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    {place.phone}
                  </p>
                )}
                {place.sourceUrl && (
                  <a
                    href={place.sourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-emerald-600 hover:text-emerald-700"
                  >
                    <ExternalLink className="w-4 h-4" />
                    View on {place.sourceName}
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
